import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { setPassword as setPasswordApi } from '@/api/user.api';
import { useToast } from '@/contexts/toast-context';
import { useAuth } from '@/hooks/use-auth';
import { TOAST_TYPES } from '@/constants/common.constants';
import { initializeFCMForUser, PUSH_ROLES } from '@/lib/fcm/init';
import userIsAuthorized from '@/utils/userIsAuthorized';

import { Card, CardContent, Button, Label, Input } from '@/components/ui';
import { Spinner, AuthFooterLinks } from '../../components';

import '../user-activate/$uuid/user-activate-style.scss';

export const Route = createFileRoute('/set-password/')({
  component: SetPassword,
});

/**
 * Shown after a driver signs in with a temporary password. Unlike the activation screen
 * — which is public and keyed by a UUID in an emailed link — this one is reached only
 * after authentication, and the server refuses every other endpoint until it is done.
 */
function SetPassword() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, updateUserProfile } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  const handleSubmit = async () => {
    const newErrors: typeof errors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setIsSubmitting(true);
      await setPasswordApi(password);

      // The session is already valid — only the pending flag changes, so there is no
      // need to sign in again (spec 4.4.1: log the driver in and go to the main page).
      updateUserProfile({ mustSetPassword: false });

      if (userIsAuthorized(user?.role, PUSH_ROLES)) {
        initializeFCMForUser();
      }

      addToast('Your password has been set. Welcome to Qluu!', {
        type: TOAST_TYPES.SUCCESS,
      });
      navigate({ to: '/dashboard' });
    } catch (err: unknown) {
      setIsSubmitting(false);
      addToast(
        err instanceof Error ? err.message : 'Failed to set your password',
        { type: TOAST_TYPES.ERROR },
      );
    }
  };

  return (
    <div className="user-activate-container">
      <div className="user-activate-content">
        <Card className="user-activate-card">
          <CardContent>
            <div className="user-activate-header">
              <img
                src="/full-logo.svg"
                alt="Logo"
                className="user-activate-logo"
              />
              <h1>Create Your Password</h1>
              <p className="user-activate-welcome">
                You signed in with a temporary password. Please create a new
                password to continue.
              </p>
            </div>

            {/* New Password */}
            <div className="user-activate-field">
              <Label>New Password</Label>
              <div className="user-activate-password-wrapper">
                <Input
                  value={password}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors((prev) => ({ ...prev, password: undefined }));
                    }
                  }}
                  // Validate on blur as well as on submit, matching the activation
                  // screen — people should learn the rule when they leave the field,
                  // not only after pressing the button.
                  onBlur={() => {
                    if (!password) {
                      setErrors((prev) => ({
                        ...prev,
                        password: 'Password is required',
                      }));
                    } else if (password.length < 8) {
                      setErrors((prev) => ({
                        ...prev,
                        password: 'Password must be at least 8 characters',
                      }));
                    }
                    // Clear a stale mismatch once the two fields agree again.
                    if (
                      confirmPassword &&
                      password === confirmPassword &&
                      errors.confirmPassword === 'Passwords do not match'
                    ) {
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword: undefined,
                      }));
                    }
                  }}
                />
                <button
                  type="button"
                  className="basic-primary user-activate-password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="invalid-feedback">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="user-activate-field">
              <Label>Confirm Password</Label>
              <div className="user-activate-password-wrapper">
                <Input
                  value={confirmPassword}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) {
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword: undefined,
                      }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit();
                  }}
                  onBlur={() => {
                    if (!confirmPassword) {
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword: 'Confirm password is required',
                      }));
                    } else if (confirmPassword.length < 8) {
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword:
                          'Password must be at least 8 characters',
                      }));
                    } else if (password !== confirmPassword) {
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword: 'Passwords do not match',
                      }));
                    }
                  }}
                />
                <button
                  type="button"
                  className="basic-primary user-activate-password-toggle"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label="Toggle password visibility"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="invalid-feedback">{errors.confirmPassword}</p>
              )}
            </div>

            <p className="help-text">
              Note: Password must be a minimum of 8 characters
            </p>

            <Button
              variant="basic-primary"
              className="user-activate-submit-button"
              size="medium"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting && <Spinner size="small" />}
              Save and Continue
            </Button>
          </CardContent>
        </Card>
        <AuthFooterLinks />
      </div>
    </div>
  );
}
